/**
 * Suggested Appointments Service
 *
 * Manages suggested appointments for baby mode:
 * - Generate suggestions on birth
 * - CRUD operations
 * - Status management
 */

import { supabase } from '@/lib/supabase';
import {
  ALL_BABY_APPOINTMENTS,
  calculateAppointmentDates,
  type SuggestionType,
} from '@/features/calendar/data/babyAppointments';

// Types
export interface SuggestedAppointment {
  id: string;
  pregnancy_id: string;
  suggestion_type: SuggestionType;
  title: string;
  description: string | null;
  recommended_from: string;
  recommended_until: string | null;
  status: 'pending' | 'scheduled' | 'completed' | 'dismissed';
  appointment_id: string | null;
  remind_at: string | null;
  priority: number;
  category_order: number;
  baby_index: number;
  created_at: string;
  // Computed fields from RPC
  is_due?: boolean;
  is_overdue?: boolean;
  days_until_due?: number;
}

export interface SuggestedAppointmentCounts {
  total: number;
  due_now: number;
  upcoming: number;
}

export interface CreateSuggestionInput {
  pregnancy_id: string;
  suggestion_type: SuggestionType;
  title: string;
  description?: string;
  recommended_from: string;
  recommended_until?: string | null;
  priority?: number;
  category_order?: number;
  baby_index?: number;
}

// ============================================================================
// GET Operations
// ============================================================================

/**
 * Get all suggested appointments for a pregnancy
 */
export async function getSuggestedAppointments(
  pregnancyId: string,
  options?: {
    status?: 'pending' | 'scheduled' | 'completed' | 'dismissed';
    type?: SuggestionType;
  }
): Promise<{ data: SuggestedAppointment[]; error: string | null }> {
  const { data, error } = await supabase.rpc('get_suggested_appointments', {
    p_pregnancy_id: pregnancyId,
    p_status: options?.status || null,
    p_suggestion_type: options?.type || null,
  });

  if (error) {
    console.error('Error fetching suggested appointments:', error);
    return { data: [], error: error.message };
  }

  return { data: data || [], error: null };
}

/**
 * Get pending suggestion counts for badges
 */
export async function getPendingSuggestionCounts(
  pregnancyId: string
): Promise<{ data: SuggestedAppointmentCounts | null; error: string | null }> {
  const { data, error } = await supabase.rpc('count_pending_suggestions', {
    p_pregnancy_id: pregnancyId,
  });

  if (error) {
    console.error('Error counting pending suggestions:', error);
    return { data: null, error: error.message };
  }

  // RPC returns an array with one row
  const counts = data?.[0] || { total: 0, due_now: 0, upcoming: 0 };
  return { data: counts, error: null };
}

/**
 * Get a single suggestion by ID
 */
export async function getSuggestedAppointment(
  suggestionId: string
): Promise<{ data: SuggestedAppointment | null; error: string | null }> {
  const { data, error } = await supabase
    .from('suggested_appointments')
    .select('*')
    .eq('id', suggestionId)
    .single();

  if (error) {
    console.error('Error fetching suggestion:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

// ============================================================================
// CREATE Operations
// ============================================================================

/**
 * Create a single suggestion
 */
export async function createSuggestion(
  input: CreateSuggestionInput
): Promise<{ data: SuggestedAppointment | null; error: string | null }> {
  const { data, error } = await supabase
    .from('suggested_appointments')
    .insert({
      pregnancy_id: input.pregnancy_id,
      suggestion_type: input.suggestion_type,
      title: input.title,
      description: input.description || null,
      recommended_from: input.recommended_from,
      recommended_until: input.recommended_until || null,
      priority: input.priority ?? 0,
      category_order: input.category_order ?? 0,
      baby_index: input.baby_index ?? 0,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating suggestion:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Generate all suggestions for a new baby based on birth date
 */
export async function generateBabyAppointmentSuggestions(
  pregnancyId: string,
  birthDate: Date,
  babyIndex: number = 0
): Promise<{ count: number; error: string | null }> {
  // Generate suggestions from templates
  const suggestions = ALL_BABY_APPOINTMENTS.map((template) => {
    const dates = calculateAppointmentDates(template, birthDate);

    return {
      pregnancy_id: pregnancyId,
      suggestion_type: template.suggestion_type,
      title: template.title,
      description: template.description,
      recommended_from: dates.recommended_from.toISOString().split('T')[0],
      recommended_until: dates.recommended_until
        ? dates.recommended_until.toISOString().split('T')[0]
        : null,
      priority: template.priority,
      category_order: template.category_order,
      baby_index: babyIndex,
    };
  });

  // Filter out suggestions that are already past their window (for late registrations)
  const today = new Date();
  const validSuggestions = suggestions.filter((s) => {
    // Keep if no end date, or end date is in the future
    if (!s.recommended_until) return true;
    return new Date(s.recommended_until) >= today;
  });

  if (validSuggestions.length === 0) {
    return { count: 0, error: null };
  }

  const { error } = await supabase
    .from('suggested_appointments')
    .insert(validSuggestions);

  if (error) {
    console.error('Error generating suggestions:', error);
    return { count: 0, error: error.message };
  }

  return { count: validSuggestions.length, error: null };
}

/**
 * Check if suggestions already exist for a pregnancy
 */
export async function hasSuggestions(
  pregnancyId: string
): Promise<{ hasData: boolean; error: string | null }> {
  const { count, error } = await supabase
    .from('suggested_appointments')
    .select('id', { count: 'exact', head: true })
    .eq('pregnancy_id', pregnancyId);

  if (error) {
    console.error('Error checking suggestions:', error);
    return { hasData: false, error: error.message };
  }

  return { hasData: (count || 0) > 0, error: null };
}

// ============================================================================
// UPDATE Operations
// ============================================================================

/**
 * Mark a suggestion as scheduled and link to appointment
 */
export async function scheduleSuggestion(
  suggestionId: string,
  appointmentId: string
): Promise<{ data: SuggestedAppointment | null; error: string | null }> {
  const { data, error } = await supabase.rpc('schedule_suggestion', {
    p_suggestion_id: suggestionId,
    p_appointment_id: appointmentId,
  });

  if (error) {
    console.error('Error scheduling suggestion:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Mark a suggestion as completed
 */
export async function completeSuggestion(
  suggestionId: string
): Promise<{ data: SuggestedAppointment | null; error: string | null }> {
  const { data, error } = await supabase.rpc('update_suggestion_status', {
    p_suggestion_id: suggestionId,
    p_status: 'completed',
    p_remind_at: null,
  });

  if (error) {
    console.error('Error completing suggestion:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Dismiss a suggestion
 */
export async function dismissSuggestion(
  suggestionId: string
): Promise<{ data: SuggestedAppointment | null; error: string | null }> {
  const { data, error } = await supabase.rpc('update_suggestion_status', {
    p_suggestion_id: suggestionId,
    p_status: 'dismissed',
    p_remind_at: null,
  });

  if (error) {
    console.error('Error dismissing suggestion:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Snooze a suggestion (remind later)
 */
export async function snoozeSuggestion(
  suggestionId: string,
  remindAt: Date
): Promise<{ data: SuggestedAppointment | null; error: string | null }> {
  const { data, error } = await supabase
    .from('suggested_appointments')
    .update({
      remind_at: remindAt.toISOString().split('T')[0],
    })
    .eq('id', suggestionId)
    .select()
    .single();

  if (error) {
    console.error('Error snoozing suggestion:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Reset a suggestion back to pending (undo complete/dismiss)
 */
export async function resetSuggestion(
  suggestionId: string
): Promise<{ data: SuggestedAppointment | null; error: string | null }> {
  const { data, error } = await supabase.rpc('update_suggestion_status', {
    p_suggestion_id: suggestionId,
    p_status: 'pending',
    p_remind_at: null,
  });

  if (error) {
    console.error('Error resetting suggestion:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

// ============================================================================
// DELETE Operations
// ============================================================================

/**
 * Delete a suggestion
 */
export async function deleteSuggestion(
  suggestionId: string
): Promise<{ success: boolean; error: string | null }> {
  const { error } = await supabase
    .from('suggested_appointments')
    .delete()
    .eq('id', suggestionId);

  if (error) {
    console.error('Error deleting suggestion:', error);
    return { success: false, error: error.message };
  }

  return { success: true, error: null };
}

/**
 * Delete all suggestions for a pregnancy
 */
export async function deleteAllSuggestions(
  pregnancyId: string
): Promise<{ success: boolean; error: string | null }> {
  const { error } = await supabase
    .from('suggested_appointments')
    .delete()
    .eq('pregnancy_id', pregnancyId);

  if (error) {
    console.error('Error deleting all suggestions:', error);
    return { success: false, error: error.message };
  }

  return { success: true, error: null };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Group suggestions by status category for UI
 */
export function groupSuggestionsByStatus(
  suggestions: SuggestedAppointment[]
): {
  dueNow: SuggestedAppointment[];
  upcoming: SuggestedAppointment[];
  completed: SuggestedAppointment[];
  dismissed: SuggestedAppointment[];
} {
  const dueNow: SuggestedAppointment[] = [];
  const upcoming: SuggestedAppointment[] = [];
  const completed: SuggestedAppointment[] = [];
  const dismissed: SuggestedAppointment[] = [];

  for (const s of suggestions) {
    if (s.status === 'completed' || s.status === 'scheduled') {
      completed.push(s);
    } else if (s.status === 'dismissed') {
      dismissed.push(s);
    } else if (s.is_due || s.is_overdue) {
      dueNow.push(s);
    } else {
      upcoming.push(s);
    }
  }

  return { dueNow, upcoming, completed, dismissed };
}

/**
 * Group suggestions by type for UI
 */
export function groupSuggestionsByType(
  suggestions: SuggestedAppointment[]
): Record<SuggestionType, SuggestedAppointment[]> {
  const groups: Record<SuggestionType, SuggestedAppointment[]> = {
    u_examination: [],
    vaccination: [],
    official: [],
    course: [],
    maternal_care: [],
  };

  for (const s of suggestions) {
    if (groups[s.suggestion_type]) {
      groups[s.suggestion_type].push(s);
    }
  }

  return groups;
}
